import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  PieChart, Edit, Send, RotateCcw, FileDown, CopyPlus
} from 'lucide-react';
import { usePatient } from '../../../context/patientContext';
import { useAuth } from '../../../context/authcontext';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { GenericListView, DisciplinePill, getDisciplinePillClassName, DocStatusPill, getDocStatusConfig, getDocStatusPillClassName } from '../../common';
import ErrorDialog from '../../common/ErrorDialog';
import ReasonDialog from '../../common/ReasonDialog';
import VersionBadge from '../../common/VersionBadge';
import { useToast } from '../../common/Toast';
import { isApiError } from '../../../dtos/common/api.dto';
import { describeApiError, getMissingFields, hasApiErrorCode } from '../../../utils/apiErrors';
import type { PermissionString } from '../../../types/permissions';
import NursingCarePlanForm from './NursingCarePlanForm';
import PTCarePlanForm from './PTCarePlanForm';
import OTCarePlanForm from './OTCarePlanForm';
import STCarePlanForm from './STCarePlanForm';
import {
  CarePlansService,
  CarePlanListItem,
  CarePlanDiscipline,
  CarePlanStatus,
  CarePlanDetail
} from '../../../services/patientmr/careplans.service';
import TemplateCarePlanForm from '../templates/TemplateCarePlanForm';
import { templateDisciplines, templateFor } from '../templates/templateUtils';
import { getDisciplineColors } from '../../../constants/disciplines';
import { useServicesOffered } from '../../../hooks/useServicesOffered';

interface CarePlanProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  visitId?: string;
  provider?: string;
}

// Utility function to extract provider name from provider object or string
const extractProviderName = (provider: any): string => {
  if (typeof provider === 'string') {
    return provider;
  }
  
  if (provider && typeof provider === 'object') {
    // Try different combinations of name fields
    if (provider.firstName && provider.lastName) {
      return `${provider.firstName} ${provider.lastName}`;
    }
    if (provider.name) {
      return provider.name;
    }
    if (provider.email) {
      return provider.email;
    }
    if (provider.displayName) {
      return provider.displayName;
    }
  }
  
  return 'Unknown Provider';
};

const CarePlan: React.FC<CarePlanProps> = (props) => {
  const servicesOffered = useServicesOffered();
  const { expanded, toggleExpanded, patientId, visitId, provider } = props;
  
  // Check if user has permission to create care plans
  const canCreateCareplan = useHasAnyPermission([
    'patientmr:phi:careplan:create' as any,
    'patientmr:phi:careplan:createall' as any
  ]);

  // Check if user has permission to read care plans
  const canReadCareplan = useHasAnyPermission([
    'patientmr:phi:careplan:read' as any,
    'patientmr:phi:careplan:readall' as any
  ]);

  // Check if user has permission to update care plans
  const canUpdateCareplan = useHasAnyPermission([
    'patientmr:phi:careplan:update' as any,
    'patientmr:phi:careplan:updateall' as any
  ]);

  // Read-only mode: has read but no write permissions
  const isReadOnlyView = canReadCareplan && !canCreateCareplan && !canUpdateCareplan;

  // QA reviewers / supervisors can start QA on a submitted care plan (Phase 4, §7A)
  const canQACareplan = useHasAnyPermission([
    'patientmr:qa' as PermissionString,
    'patientmr:phi:supervisor:finalize' as PermissionString,
    'patientmr:phi:supervisor:approve' as PermissionString
  ]);

  const { showToast } = useToast();
  
  // Pagination constants
  const CAREPLANS_PER_PAGE = 6;
  
  // Get patient data from context
  const { selectedPatient: contextPatient } = usePatient();
  
  // Get current provider information from auth context
  const { user } = useAuth();
  
  // Use patient from props if provided, otherwise use from context
  const patientData = React.useMemo(() => 
    patientId ? { id: patientId } : contextPatient
  , [patientId, contextPatient]);

  // Active tabs: 'past', 'new', or 'select_type'
  const [activeTab, setActiveTab] = useState('past');
  
  // Selected care plan type
  const [selectedCarePlanType, setSelectedCarePlanType] = useState<string | null>(null);
  
  // State management for care plans
  const [carePlans, setCarePlans] = useState<CarePlanListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false); // Track if we've loaded data at least once
  const [saving, setSaving] = useState<boolean>(false); // Track save/update operations
  const [submitting, setSubmitting] = useState<boolean>(false); // Track submit operations
  const [reopening, setReopening] = useState<boolean>(false); // Track reopen operations
  
  // Error dialog state for save failures
  const [saveErrorDialogOpen, setSaveErrorDialogOpen] = useState<boolean>(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>('');
  const [saveErrorDetails, setSaveErrorDetails] = useState<string>('');
  const [lastFormData, setLastFormData] = useState<any>(null); // Store form data for retry
  
  // Pagination state
  const [paginationToken, setPaginationToken] = useState<string | undefined>(undefined);
  const [hasMoreCarePlans, setHasMoreCarePlans] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // State for current care plan details when editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<CarePlanDetail | null>(null);
  
  // State for manual refresh functionality
  const [refreshing, setRefreshing] = useState<boolean>(false);
  
  // State to track if there's an active visit
  const [hasActiveVisit, setHasActiveVisit] = useState<boolean>(!!visitId);

  // Ref for deduplication
  const hasLoadedCarePlansRef = useRef<string | null>(null);

  // Phase 4: reason prompts, QA start, read-only past versions, submit field list
  const [reasonDialog, setReasonDialog] = useState<{ kind: 'reopen' | 'reject'; carePlanId: string } | null>(null);
  const [startingQA, setStartingQA] = useState<boolean>(false);
  const [viewingVersion, setViewingVersion] = useState<{ version: number; detail: CarePlanDetail } | null>(null);
  const [loadingVersion, setLoadingVersion] = useState<boolean>(false);
  const [submitBlock, setSubmitBlock] = useState<{ message: string; details: string } | null>(null);

  // Stable form data: the forms copy existingData into state whenever its identity changes,
  // so it must not be rebuilt on every render
  const formExistingData = useMemo(() => {
    const source = viewingVersion?.detail ?? editingData;
    return source ? CarePlansService.mapCarePlanToFormData(source) : null;
  }, [editingData, viewingVersion]);
  
  // Phase 19 F26: the shown record's certification period (read-only dates on the plan)
  const formCertificationPeriod = (viewingVersion?.detail ?? editingData)?.certificationPeriod ?? null;

  // Effect to update active visit state when visitId changes
  useEffect(() => {
    setHasActiveVisit(!!visitId);
    // Reset hasLoadedOnce when visitId changes so care plans are reloaded for the new visit
    if (visitId) {
      setHasLoadedOnce(false);
    }
    console.log('CarePlan: visitId changed', { visitId, hasActiveVisit: !!visitId });
  }, [visitId]);
  
  // Debug effect for monitoring state changes
  useEffect(() => {
    console.log('CarePlan component state:', { 
      patientId: patientData?.id,
      visitId,
      hasActiveVisit,
    });
  }, [patientData?.id, visitId, hasActiveVisit]);
  
  // Handle save care plan from form
  const handleSaveCarePlan = async (formData: any, submitAfterSave = false) => {
    // Store form data for potential retry
    setLastFormData(formData);

    try {
      setSaving(true);
      setError(null);

      const currentPatientId = patientData?.id;
      // Use the provider from props, or fallback to user data or a default
      const currentProvider = provider || (user ? extractProviderName(user) : 'Current Provider');

      // Check if we have the required IDs
      if (!currentPatientId) {
        throw new Error('Patient ID is required to save a care plan');
      }

      // Log the state for debugging
      console.log('handleSaveCarePlan check:', { visitId, hasActiveVisit, editingId, submitAfterSave });

      // Map the form data to the care plan structure
      const disciplineType = mapCategoryToDiscipline(selectedCarePlanType);
      const carePlanData = CarePlansService.mapFormDataToCarePlan(formData, disciplineType, currentProvider);

      let savedCarePlanId: string | undefined;
      let prefilledCount = 0;

      if (editingId) {
        // Update existing care plan
        const updatedCarePlan = await CarePlansService.updateCarePlan(
          currentPatientId,
          editingId,
          {
            ...carePlanData,
            carePlanId: editingId
          }
        );
        savedCarePlanId = editingId;

        // Update the local state
        setCarePlans(prevCarePlans =>
          prevCarePlans.map(carePlan =>
            carePlan.carePlanId === editingId
              ? {
                  carePlanId: updatedCarePlan.carePlanId || '',
                  date: new Date().toISOString(),
                  status: updatedCarePlan.status || CarePlanStatus.DRAFT,
                  discipline: updatedCarePlan.discipline,
                  providerName: extractProviderName(updatedCarePlan.provider)
                }
              : carePlan
          )
        );
      } else {
        // Create new care plan
        // Phase 19 F11: the first save of a plan linked to a completed assessment fills its blank fields
        const newCarePlan = await CarePlansService.createCarePlan(
          currentPatientId,
          visitId || 'NO_VISIT',
          carePlanData,
          { prefill: Boolean(carePlanData.data?.assessmentId) }
        );
        savedCarePlanId = newCarePlan.carePlanId;
        prefilledCount = Object.keys(newCarePlan.prefillSources || {}).length;

        // Add to the local state
        if (newCarePlan.carePlanId) {
          // Ensure providerName is a string - handle cases where provider might be an object
          const providerName = extractProviderName(newCarePlan.provider);

          const newListItem: CarePlanListItem = {
            carePlanId: newCarePlan.carePlanId,
            date: new Date().toISOString(),
            status: newCarePlan.status || CarePlanStatus.DRAFT,
            discipline: newCarePlan.discipline,
            providerName: providerName
          };

          setCarePlans(prev => [newListItem, ...prev]);
        }
      }

      // If Submit was clicked, submit immediately after save
      if (submitAfterSave && savedCarePlanId) {
        setSaving(false);
        const submitted = await handleSubmitCarePlan(savedCarePlanId);
        if (!submitted) {
          // Saved as a draft but not submitted: keep the form open on the saved record
          setLastFormData(null);
          if (!editingId) setEditingId(savedCarePlanId);
          return;
        }
      }

      // Clear stored form data on success
      setLastFormData(null);

      // Phase 19 F11: reopen a plan that was just filled from its assessment so the clinician reviews it
      if (prefilledCount > 0 && savedCarePlanId) {
        showToast(`Filled ${prefilledCount} field${prefilledCount === 1 ? '' : 's'} from the assessment. Review them before submitting.`, { variant: 'success' });
        setSaving(false);
        await handleEditCarePlan(savedCarePlanId);
        return;
      }

      // Reset form fields
      setEditingId(null);
      setEditingData(null);
      setSelectedCarePlanType(null);

      // Refresh the entire care plans list to ensure we have the latest data
      try {
        console.log(`Auto-refreshing care plans after save operation for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''}`);
        const response = await CarePlansService.getCarePlans(currentPatientId, visitId, {
          limit: CAREPLANS_PER_PAGE.toString()
        });

        if (!response.carePlans || response.carePlans.length === 0) {
          console.log(`No care plans found for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''} after save operation`);
          setCarePlans([]);
        } else {
          console.log(`Loaded ${response.carePlans.length} care plans for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''} after save operation`);
          setCarePlans(response.carePlans);
        }
        setPaginationToken(response.paginationToken);
        setHasMoreCarePlans(response.hasMore);
      } catch (refreshErr) {
        console.error('Error refreshing care plans after save:', refreshErr);
        // We already have local state updates, so we won't show an error for refresh failure
      }

      // Switch to past care plans tab
      setActiveTab('past');
    } catch (err: any) {
      console.error('Error saving care plan:', err);
      
      // Determine error message
      let errorMessage = 'Failed to save care plan. Please try again.';
      let errorDetails = '';
      
      if (isApiError(err)) {
        errorMessage = describeApiError(err, errorMessage);
      } else if (err.message && err.message.includes('No active visit found')) {
        errorMessage = 'Cannot save care plan: No active visit found.';
        errorDetails = 'Please start a visit first before saving the care plan.';
      } else if (err.message && err.message.includes('Patient ID is required')) {
        errorMessage = 'Cannot save care plan: Patient information is missing.';
        errorDetails = 'Please ensure a patient is selected.';
      } else if (err.response && err.response.status === 401) {
        errorMessage = 'Authentication error.';
        errorDetails = 'Your session may have expired. Please log in again.';
      } else if (err.response && err.response.status === 403) {
        errorMessage = 'Permission denied.';
        errorDetails = 'You do not have permission to save this care plan.';
      } else if (err.response && err.response.status >= 500) {
        errorMessage = 'Server error.';
        errorDetails = 'The server encountered an error. Please try again later.';
      } else if (err.message) {
        errorDetails = err.message;
      }
      
      // Show error dialog instead of inline error
      setSaveErrorMessage(errorMessage);
      setSaveErrorDetails(errorDetails);
      setSaveErrorDialogOpen(true);
      
      // Also set the inline error for the list view
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Retry save with stored form data
  const handleRetrySave = () => {
    if (lastFormData) {
      handleSaveCarePlan(lastFormData);
    }
  };

  // Start editing care plan
  // Phase 19 F16: printable care plan
  const handleDownloadCarePlanPdf = async (carePlanId: string) => {
    if (!patientData?.id) return;
    try {
      const { url } = await CarePlansService.getCarePlanPdfUrl(patientData.id, carePlanId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(describeApiError(err, 'Could not prepare the care plan PDF.'));
    }
  };

  // Phase 19 F14: a revision / recertification plan starts as a copy of a completed plan
  const handleStartRevision = async (carePlan: CarePlanListItem) => {
    if (!patientData?.id || !carePlan.carePlanId) return;
    try {
      setSaving(true);
      const providerName = provider || (user ? extractProviderName(user) : 'Current Provider');
      const created = await CarePlansService.createCarePlan(
        patientData.id,
        visitId || 'NO_VISIT',
        { provider: providerName, discipline: carePlan.discipline, data: {} },
        { copyFromId: carePlan.carePlanId }
      );
      showToast('Revision started from the completed plan. Update goal status and changes, then submit.', { variant: 'success' });
      if (created.carePlanId) await handleEditCarePlan(created.carePlanId);
    } catch (err) {
      setError(describeApiError(err, 'Could not start a revision.'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditCarePlan = async (carePlanId: string) => {
    try {
      setLoading(true);
      setError(null); // Clear any existing errors
      
      const currentPatientId = patientData?.id;
      if (!currentPatientId) {
        throw new Error('No patient selected');
      }
      
      // Fetch the care plan details - this is a different API call for a specific care plan,
      // so it's ok to keep this as it's not a duplicate call to get all care plans
      const carePlanDetail = await CarePlansService.getCarePlanDetails(currentPatientId, carePlanId);

      // Only switch to edit mode if we successfully got the care plan details
      setViewingVersion(null);
      setEditingId(carePlanId);
      setEditingData(carePlanDetail);
      
      // Map discipline to UI category
      const category = mapDisciplineToCategory(carePlanDetail.discipline);
      setSelectedCarePlanType(category);
      setActiveTab('new');
    } catch (err: any) {
      console.error('Error starting care plan edit:', err);
      
      // Show error banner but keep the list visible - don't switch to edit mode
      if (err.response && err.response.status === 404) {
        setError('Care plan not found. It may have been deleted.');
      } else if (err.response && err.response.status === 401) {
        setError('Authentication error. Please log in again.');
      } else if (err.response && err.response.status === 403) {
        setError('You do not have permission to edit this care plan.');
      } else {
        setError('Failed to load care plan for editing. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Submit care plan. Resolves true when submitted.
  const handleSubmitCarePlan = async (carePlanId: string): Promise<boolean> => {
    try {
      setSubmitting(true);
      setError(null);
      
      const currentPatientId = patientData?.id;
      if (!currentPatientId) {
        throw new Error('No patient selected');
      }
      
      // Call service to submit the care plan
      await CarePlansService.submitCarePlan(currentPatientId, carePlanId);
      
      // Update the local state immediately for a responsive UI
      setCarePlans(prevCarePlans => 
        prevCarePlans.map(carePlan => 
          carePlan.carePlanId === carePlanId 
            ? { ...carePlan, status: CarePlanStatus.SUBMITTED } 
            : carePlan
        )
      );
      
      // Update editing data if this is the care plan being edited
      if (editingId === carePlanId && editingData) {
        setEditingData({
          ...editingData,
          status: CarePlanStatus.SUBMITTED
        });
      }
      
      // Refresh the entire list to ensure we have the latest data
      try {
        const response = await CarePlansService.getCarePlans(currentPatientId, visitId, {
          limit: CAREPLANS_PER_PAGE.toString()
        });
        if (!response.carePlans || response.carePlans.length === 0) {
          console.warn('No care plans returned after submission');
        } else {
          setCarePlans(response.carePlans);
        }
        setPaginationToken(response.paginationToken);
        setHasMoreCarePlans(response.hasMore);
      } catch (refreshErr) {
        console.error('Error refreshing care plans after submission:', refreshErr);
      }
      showToast('Care plan submitted.', { variant: 'success' });
      return true;
    } catch (err: unknown) {
      console.error('Error submitting care plan');
      if (hasApiErrorCode(err, 'REQUIRED_FIELDS_MISSING')) {
        const missing = getMissingFields(err);
        setSubmitBlock({
          message: 'Required fields are still empty. The care plan stays a draft until they are completed.',
          details: missing.length > 0 ? missing.join('\n') : describeApiError(err),
        });
      } else {
        const message = describeApiError(err, 'Failed to submit care plan. Please try again.');
        setError(message);
        showToast(message, { variant: 'error' });
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reopen (from COMPLETE) or reject (from SUBMITTED/QA); the reason comes from the ReasonDialog
  const handleReopenCarePlan = async (carePlanId: string, reason: string) => {
    try {
      setReopening(true);
      setError(null);
      
      const currentPatientId = patientData?.id;
      if (!currentPatientId) {
        throw new Error('No patient selected');
      }
      
      // Call service to reopen the care plan
      await CarePlansService.reopenCarePlan(currentPatientId, carePlanId, reason);
      setReasonDialog(null);
      
      // Update the local state immediately for a responsive UI
      setCarePlans(prevCarePlans => 
        prevCarePlans.map(carePlan => 
          carePlan.carePlanId === carePlanId 
            ? { ...carePlan, status: CarePlanStatus.DRAFT } 
            : carePlan
        )
      );
      
      // Reload the open care plan so the new version number shows
      if (editingId === carePlanId && editingData) {
        try {
          const fresh = await CarePlansService.getCarePlanDetails(currentPatientId, carePlanId);
          setEditingData(fresh);
        } catch {
          setEditingData({ ...editingData, status: CarePlanStatus.DRAFT });
        }
        setViewingVersion(null);
      }
      showToast('Care plan reopened as a draft.', { variant: 'success' });
      
      // Refresh the entire list to ensure we have the latest data
      try {
        const response = await CarePlansService.getCarePlans(currentPatientId, visitId, {
          limit: CAREPLANS_PER_PAGE.toString()
        });
        if (!response.carePlans || response.carePlans.length === 0) {
          console.warn('No care plans returned after reopening');
        } else {
          setCarePlans(response.carePlans);
        }
        setPaginationToken(response.paginationToken);
        setHasMoreCarePlans(response.hasMore);
      } catch (refreshErr) {
        console.error('Error refreshing care plans after reopening:', refreshErr);
      }
    } catch (err: unknown) {
      console.error('Error reopening care plan');
      showToast(describeApiError(err, 'Failed to reopen care plan. Please try again.'), { variant: 'error' });
    } finally {
      setReopening(false);
    }
  };

  // Start QA review (SUBMITTED -> QA)
  const handleStartQACarePlan = async (carePlanId: string) => {
    const currentPatientId = patientData?.id;
    if (!currentPatientId) return;
    try {
      setStartingQA(true);
      await CarePlansService.startQACarePlan(currentPatientId, carePlanId);
      setCarePlans(prev => prev.map(cp => (cp.carePlanId === carePlanId ? { ...cp, status: CarePlanStatus.QA } : cp)));
      setEditingData(prev => (prev && editingId === carePlanId ? { ...prev, status: CarePlanStatus.QA } : prev));
      showToast('QA review started.', { variant: 'success' });
    } catch (err: unknown) {
      console.error('Error starting care plan QA');
      showToast(describeApiError(err, 'Failed to start QA. Please try again.'), { variant: 'error' });
    } finally {
      setStartingQA(false);
    }
  };

  // Autosave: update the draft without closing the form or refreshing the list
  const handleAutosaveCarePlan = async (formData: Record<string, unknown>) => {
    const currentPatientId = patientData?.id;
    if (!currentPatientId || !editingId) return;
    const currentProvider = provider || (user ? extractProviderName(user) : 'Current Provider');
    const disciplineType = mapCategoryToDiscipline(selectedCarePlanType);
    const carePlanData = CarePlansService.mapFormDataToCarePlan(formData, disciplineType, currentProvider);
    await CarePlansService.updateCarePlan(currentPatientId, editingId, { ...carePlanData, carePlanId: editingId });
  };

  // Load a read-only earlier version (GET ?version=n)
  const handleViewVersion = async (version: number) => {
    const currentPatientId = patientData?.id;
    if (!currentPatientId || !editingId) return;
    if (editingData?.version && version >= editingData.version) {
      setViewingVersion(null);
      return;
    }
    try {
      setLoadingVersion(true);
      const detail = await CarePlansService.getCarePlanDetails(currentPatientId, editingId, version);
      setViewingVersion({ version, detail });
    } catch (err: unknown) {
      console.error('Error loading care plan version');
      showToast(describeApiError(err, 'Could not load that version.'), { variant: 'error' });
    } finally {
      setLoadingVersion(false);
    }
  };
  
  // Approve/Finalize care plan
  const handleApproveCarePlan = async (carePlanId: string) => {
    try {
      setSubmitting(true);
      setError(null);
      
      const currentPatientId = patientData?.id;
      if (!currentPatientId) {
        throw new Error('No patient selected');
      }
      
      // Call service to finalize the care plan
      await CarePlansService.finalizeCarePlan(currentPatientId, carePlanId);
      
      // Update the local state immediately for a responsive UI
      setCarePlans(prevCarePlans => 
        prevCarePlans.map(carePlan => 
          carePlan.carePlanId === carePlanId 
            ? { ...carePlan, status: CarePlanStatus.COMPLETE } 
            : carePlan
        )
      );
      
      // Update editing data if this is the care plan being edited
      if (editingId === carePlanId && editingData) {
        setEditingData({
          ...editingData,
          status: CarePlanStatus.COMPLETE
        });
      }
      
      // Refresh the entire list to ensure we have the latest data
      try {
        const response = await CarePlansService.getCarePlans(currentPatientId, visitId, {
          limit: CAREPLANS_PER_PAGE.toString()
        });
        if (!response.carePlans || response.carePlans.length === 0) {
          console.warn('No care plans returned after approval');
        } else {
          setCarePlans(response.carePlans);
        }
        setPaginationToken(response.paginationToken);
        setHasMoreCarePlans(response.hasMore);
      } catch (refreshErr) {
        console.error('Error refreshing care plans after approval:', refreshErr);
      }
    } catch (err: unknown) {
      console.error('Error approving care plan');
      // SELF_APPROVAL, INVALID_TRANSITION, ... are shown as a toast
      showToast(describeApiError(err, 'Failed to approve care plan. Please try again.'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Map discipline enum to UI category string
  const mapDisciplineToCategory = (discipline: CarePlanDiscipline): string => {
    switch (discipline) {
      case CarePlanDiscipline.RN:
        return "Nursing";
      case CarePlanDiscipline.PT:
        return "PT";
      case CarePlanDiscipline.OT:
        return "OT";
      case CarePlanDiscipline.ST:
        return "ST";
      default:
        // Phase 20B: a template discipline's category is its code (MSW, HHA)
        return templateFor(discipline, 'carePlan') ? String(discipline) : "Unknown";
    }
  };
  
  // Map UI category string to discipline enum
  const mapCategoryToDiscipline = (category: string | null): CarePlanDiscipline => {
    switch (category) {
      case "Nursing":
        return CarePlanDiscipline.RN;
      case "PT":
        return CarePlanDiscipline.PT;
      case "OT":
        return CarePlanDiscipline.OT;
      case "ST":
        return CarePlanDiscipline.ST;
      default:
        // Phase 20B: template disciplines use their code as the category
        if (category && templateFor(category, 'carePlan')) return category as CarePlanDiscipline;
        return CarePlanDiscipline.RN; // Default case
    }
  };
  
  // Handle care plan type selection
  const handleSelectCarePlanType = (type: string) => {
    setViewingVersion(null);
    setEditingId(null);
    setEditingData(null);
    setSelectedCarePlanType(type);
    setActiveTab('new');
  };
  
  // Handle cancel from form
  const handleCancelForm = () => {
    setViewingVersion(null);
    setEditingId(null);
    setEditingData(null);
    setSelectedCarePlanType(null);
    setSaving(false); // Reset saving state when cancelled
    setLastFormData(null); // Clear stored form data
    setActiveTab('past');
  };
  
  // Manual refresh function - triggered by user click
  const handleRefreshCarePlans = async () => {
    const currentPatientId = patientData?.id;
    if (!currentPatientId || refreshing) return;
    
    try {
      setRefreshing(true);
      setError(null);
      console.log(`Manually refreshing care plans for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''}`);
      
      // Reset ref to allow reload
      hasLoadedCarePlansRef.current = null;
      
      // Reset pagination and fetch first page
      const response = await CarePlansService.getCarePlans(currentPatientId, visitId, {
        limit: CAREPLANS_PER_PAGE.toString()
      });
      
      // Handle null or empty response
      if (!response.carePlans || response.carePlans.length === 0) {
        console.log(`No care plans found for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''} during manual refresh`);
        setCarePlans([]);
      } else {
        console.log(`Refreshed ${response.carePlans.length} care plans for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''}`);
        setCarePlans(response.carePlans);
      }
      
      setPaginationToken(response.paginationToken);
      setHasMoreCarePlans(response.hasMore);
      
      // Always mark as loaded, even if empty
      setHasLoadedOnce(true);
      
      // Set ref after successful refresh
      const cacheKey = `${currentPatientId}-${visitId || 'all'}`;
      hasLoadedCarePlansRef.current = cacheKey;
    } catch (err: any) {
      console.error('Error refreshing care plans:', err);
      setError('Failed to refresh care plans. Please try again.');
      // Don't clear existing care plans on error
    } finally {
      setRefreshing(false);
    }
  };
  
  // Load more care plans
  const handleLoadMoreCarePlans = async () => {
    const currentPatientId = patientData?.id;
    if (!currentPatientId || !paginationToken || loadingMore) return;
    
    try {
      setLoadingMore(true);
      setError(null);
      
      const response = await CarePlansService.getCarePlans(currentPatientId, visitId, {
        limit: CAREPLANS_PER_PAGE.toString(),
        paginationToken: paginationToken
      });
      
      setCarePlans(prev => [...prev, ...response.carePlans]);
      setPaginationToken(response.paginationToken);
      setHasMoreCarePlans(response.hasMore);
    } catch (err: any) {
      console.error('Error loading more care plans:', err);
      setError('Failed to load more care plans. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };
  
  // getCategoryColor removed — use getDisciplinePillClassName(discipline) from common/DisciplinePill
  
  // getStatusColor / getStatusDisplayText removed — use DocStatusPill / getDocStatusConfig from common/Pills
  
  // Render the appropriate care plan form based on type
  const renderCarePlanForm = () => {
    if (!selectedCarePlanType) return null;

    // Case C: User lacks read permissions - show permission error
    if (!canReadCareplan) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-4xl mb-4">🔒</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Not enough permissions to view form</h3>
            <p className="text-gray-600 text-sm">
              You don't have the required permissions to view care plan forms. Contact your administrator if you need access.
            </p>
          </div>
        </div>
      );
    }

    // Get the current care plan status
    const currentStatus = viewingVersion?.detail.status || editingData?.status || CarePlanStatus.DRAFT;

    // Determine if form should be read-only.
    // Phase 4: only a DRAFT can be edited, and past versions are always read-only.
    const isReadOnly =
      (!canCreateCareplan && canReadCareplan) ||
      !!viewingVersion ||
      (!!editingId && currentStatus !== CarePlanStatus.DRAFT);
    const formKey = viewingVersion ? `${editingId}-v${viewingVersion.version}` : (editingId || 'new');

    // Version badge and picker (v2+), shown in the form header
    const currentVersion = editingData?.version ?? 1;
    const versionHeader = editingId ? (
      <>
        {viewingVersion ? (
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            Viewing v{viewingVersion.version} (read-only)
          </span>
        ) : (
          <VersionBadge version={editingData?.version} />
        )}
        {currentVersion > 1 && (
          <select
            className="border border-slate-300 rounded px-1 py-0.5 text-xs bg-white"
            value={viewingVersion ? String(viewingVersion.version) : String(currentVersion)}
            disabled={loadingVersion}
            onChange={(e) => { void handleViewVersion(Number(e.target.value)); }}
            aria-label="View version"
          >
            {Array.from({ length: currentVersion }, (_, i) => currentVersion - i).map((v) => (
              <option key={v} value={v}>
                {v === currentVersion ? `v${v} (current)` : `View v${v}`}
              </option>
            ))}
          </select>
        )}
      </>
    ) : null;
    
    switch (selectedCarePlanType) {
      case "Nursing":
        return (
          <NursingCarePlanForm 
            key={formKey}
            editingId={editingId}
            patientId={patientData?.id || ''}
            existingData={editingData ? formExistingData : null}
            certificationPeriod={editingData ? formCertificationPeriod : null}
            onSave={handleSaveCarePlan}
            onSubmit={editingId ? () => handleSubmitCarePlan(editingId) : undefined}
            onReopen={editingId && !viewingVersion && canQACareplan ? () => setReasonDialog({ kind: currentStatus === CarePlanStatus.COMPLETE ? 'reopen' : 'reject', carePlanId: editingId }) : undefined}
            onApprove={editingId && !viewingVersion && canQACareplan ? () => handleApproveCarePlan(editingId) : undefined}
            onStartQA={editingId && canQACareplan && !viewingVersion ? () => handleStartQACarePlan(editingId) : undefined}
            startingQA={startingQA}
            headerExtra={versionHeader}
            onAutosave={editingId ? handleAutosaveCarePlan : undefined}
            autosaveEnabled={!!editingId && !viewingVersion}
            onCancel={viewingVersion ? () => setViewingVersion(null) : handleCancelForm}
            saving={saving}
            submitting={submitting}
            reopening={reopening}
            carePlanStatus={currentStatus}
            isReadOnly={isReadOnly}
          />
        );
      case "PT":
        return (
          <PTCarePlanForm 
            key={formKey}
            editingId={editingId}
            patientId={patientData?.id || ''}
            existingData={editingData ? formExistingData : null}
            certificationPeriod={editingData ? formCertificationPeriod : null}
            onSave={handleSaveCarePlan}
            onSubmit={editingId ? () => handleSubmitCarePlan(editingId) : undefined}
            onReopen={editingId && !viewingVersion && canQACareplan ? () => setReasonDialog({ kind: currentStatus === CarePlanStatus.COMPLETE ? 'reopen' : 'reject', carePlanId: editingId }) : undefined}
            onApprove={editingId && !viewingVersion && canQACareplan ? () => handleApproveCarePlan(editingId) : undefined}
            onStartQA={editingId && canQACareplan && !viewingVersion ? () => handleStartQACarePlan(editingId) : undefined}
            startingQA={startingQA}
            headerExtra={versionHeader}
            onAutosave={editingId ? handleAutosaveCarePlan : undefined}
            autosaveEnabled={!!editingId && !viewingVersion}
            onCancel={viewingVersion ? () => setViewingVersion(null) : handleCancelForm}
            saving={saving}
            submitting={submitting}
            reopening={reopening}
            carePlanStatus={currentStatus}
            isReadOnly={isReadOnly}
          />
        );
      case "OT":
        return (
          <OTCarePlanForm 
            key={formKey}
            editingId={editingId}
            patientId={patientData?.id || ''}
            existingData={editingData ? formExistingData : null}
            certificationPeriod={editingData ? formCertificationPeriod : null}
            onSave={handleSaveCarePlan}
            onSubmit={editingId ? () => handleSubmitCarePlan(editingId) : undefined}
            onReopen={editingId && !viewingVersion && canQACareplan ? () => setReasonDialog({ kind: currentStatus === CarePlanStatus.COMPLETE ? 'reopen' : 'reject', carePlanId: editingId }) : undefined}
            onApprove={editingId && !viewingVersion && canQACareplan ? () => handleApproveCarePlan(editingId) : undefined}
            onStartQA={editingId && canQACareplan && !viewingVersion ? () => handleStartQACarePlan(editingId) : undefined}
            startingQA={startingQA}
            headerExtra={versionHeader}
            onAutosave={editingId ? handleAutosaveCarePlan : undefined}
            autosaveEnabled={!!editingId && !viewingVersion}
            onCancel={viewingVersion ? () => setViewingVersion(null) : handleCancelForm}
            saving={saving}
            submitting={submitting}
            reopening={reopening}
            carePlanStatus={currentStatus}
            isReadOnly={isReadOnly}
          />
        );
      case "ST":
        return (
          <STCarePlanForm 
            key={formKey}
            editingId={editingId}
            patientId={patientData?.id || ''}
            existingData={editingData ? formExistingData : null}
            certificationPeriod={editingData ? formCertificationPeriod : null}
            onSave={handleSaveCarePlan}
            onSubmit={editingId ? () => handleSubmitCarePlan(editingId) : undefined}
            onReopen={editingId && !viewingVersion && canQACareplan ? () => setReasonDialog({ kind: currentStatus === CarePlanStatus.COMPLETE ? 'reopen' : 'reject', carePlanId: editingId }) : undefined}
            onApprove={editingId && !viewingVersion && canQACareplan ? () => handleApproveCarePlan(editingId) : undefined}
            onStartQA={editingId && canQACareplan && !viewingVersion ? () => handleStartQACarePlan(editingId) : undefined}
            startingQA={startingQA}
            headerExtra={versionHeader}
            onAutosave={editingId ? handleAutosaveCarePlan : undefined}
            autosaveEnabled={!!editingId && !viewingVersion}
            onCancel={viewingVersion ? () => setViewingVersion(null) : handleCancelForm}
            saving={saving}
            submitting={submitting}
            reopening={reopening}
            carePlanStatus={currentStatus}
            isReadOnly={isReadOnly}
          />
        );
      default: {
        // Phase 20B: disciplines without a hand-built form use their clinical template
        const template = templateFor(selectedCarePlanType, 'carePlan');
        if (!template) return null;
        return (
          <TemplateCarePlanForm
            template={template} 
            key={formKey}
            editingId={editingId}
            patientId={patientData?.id || ''}
            existingData={editingData ? formExistingData : null}
            certificationPeriod={editingData ? formCertificationPeriod : null}
            onSave={handleSaveCarePlan}
            onSubmit={editingId ? () => handleSubmitCarePlan(editingId) : undefined}
            onReopen={editingId && !viewingVersion && canQACareplan ? () => setReasonDialog({ kind: currentStatus === CarePlanStatus.COMPLETE ? 'reopen' : 'reject', carePlanId: editingId }) : undefined}
            onApprove={editingId && !viewingVersion && canQACareplan ? () => handleApproveCarePlan(editingId) : undefined}
            onStartQA={editingId && canQACareplan && !viewingVersion ? () => handleStartQACarePlan(editingId) : undefined}
            startingQA={startingQA}
            headerExtra={versionHeader}
            onAutosave={editingId ? handleAutosaveCarePlan : undefined}
            autosaveEnabled={!!editingId && !viewingVersion}
            onCancel={viewingVersion ? () => setViewingVersion(null) : handleCancelForm}
            saving={saving}
            submitting={submitting}
            reopening={reopening}
            carePlanStatus={currentStatus}
            isReadOnly={isReadOnly}
          />
        );
      }
    }
  };

  // Fetch care plans when the component mounts or patientId changes
  useEffect(() => {
    const fetchCarePlans = async () => {
      const currentPatientId = patientData?.id;
      
      if (!currentPatientId) {
        console.warn('No patient ID available in care plans component');
        setError('Patient information not available. Please select a patient to view care plans.');
        return;
      }

      // Skip if already loaded for this patient+visit combination
      const cacheKey = `${currentPatientId}-${visitId || 'all'}`;
      if (hasLoadedCarePlansRef.current === cacheKey || loading) {
        return;
      }

      hasLoadedCarePlansRef.current = cacheKey;
      
      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching care plans for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''}`);
        const response = await CarePlansService.getCarePlans(currentPatientId, visitId, { 
          limit: CAREPLANS_PER_PAGE.toString() 
        });
        
        // Handle null or empty response
        if (!response.carePlans || response.carePlans.length === 0) {
          console.log(`No care plans found for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''}`);
          setCarePlans([]);
        } else {
          console.log(`Loaded ${response.carePlans.length} care plans for patient ${currentPatientId}${visitId ? ` and visit ${visitId}` : ''}`);
          setCarePlans(response.carePlans);
        }
        
        setPaginationToken(response.paginationToken);
        setHasMoreCarePlans(response.hasMore);
        
        // Mark that we've completed the load attempt, regardless of result
        setHasLoadedOnce(true);
      } catch (err: any) {
        console.error('Error fetching care plans:', err);
        
        if (err.response && err.response.status === 401) {
          setError('Authentication error. Please log in again.');
        } else if (err.response && err.response.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Failed to load care plans. Please try again.');
        }
        
        // Reset ref on error to allow retry
        hasLoadedCarePlansRef.current = null;
        setHasLoadedOnce(true);
        setCarePlans([]);
      } finally {
        setLoading(false);
      }
    };

    // Load care plans when the tab is expanded (opened)
    if (expanded) {
      fetchCarePlans();
    }
    
    // Reset state when collapsed
    if (!expanded) {
      setActiveTab('past');
      setSelectedCarePlanType(null);
      setEditingId(null);
      setEditingData(null);
      setError(null);
      setHasLoadedOnce(false); // Reset load state when collapsed
      setSaving(false); // Reset saving state when collapsed
      setLastFormData(null); // Clear stored form data
      hasLoadedCarePlansRef.current = null; // Reset ref when collapsed
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loading is only a re-entry guard; adding it would re-run the load when loading toggles
  }, [patientData?.id, expanded, visitId]);

  // Care plan type options for dropdown
  const carePlanTypeOptions = [
    {
      label: 'Private Duty Nursing',
      color: 'bg-blue-500',
      onClick: () => handleSelectCarePlanType('Nursing'),
    },
    {
      label: 'Physical Therapy',
      color: 'bg-green-500',
      onClick: () => handleSelectCarePlanType('PT'),
    },
    {
      label: 'Occupational Therapy',
      color: 'bg-orange-500',
      onClick: () => handleSelectCarePlanType('OT'),
    },
    {
      label: 'Speech Therapy',
      color: 'bg-purple-500',
      onClick: () => handleSelectCarePlanType('ST'),
    },
    // Phase 20B: template care plans of the disciplines the agency offers (MSW, home health aide)
    ...templateDisciplines('carePlan').filter(d => servicesOffered.includes(d)).map(d => ({
      label: templateFor(d, 'carePlan')!.title.replace(/ Care Plan$/, ''),
      color: getDisciplineColors(d).dot,
      onClick: () => handleSelectCarePlanType(d),
    })),
  ];

  return (
    <>
      {/* Error Dialog for Save Failures */}
      <ErrorDialog
        open={saveErrorDialogOpen}
        onClose={() => setSaveErrorDialogOpen(false)}
        title="Save Failed"
        message={saveErrorMessage}
        details={saveErrorDetails}
        onRetry={lastFormData ? handleRetrySave : undefined}
        retryLabel="Try Again"
      />

      {/* Submit blocked by the server's required-field check (422) */}
      <ErrorDialog
        open={submitBlock !== null}
        onClose={() => setSubmitBlock(null)}
        title="Cannot submit yet"
        message={submitBlock?.message || ''}
        details={submitBlock?.details}
      />

      {/* Reopen / reject: reason required (Phase 4) */}
      <ReasonDialog
        open={reasonDialog !== null}
        title={reasonDialog?.kind === 'reject' ? 'Reject care plan' : 'Reopen care plan'}
        description={
          reasonDialog?.kind === 'reject'
            ? 'The care plan goes back to the author as a draft. The reason is kept in its history.'
            : 'The care plan returns to draft and a new version is started. The reason is kept in its history.'
        }
        label={reasonDialog?.kind === 'reject' ? 'Reason for rejecting' : 'Reason for reopening'}
        confirmLabel={reasonDialog?.kind === 'reject' ? 'Reject' : 'Reopen'}
        destructive={reasonDialog?.kind === 'reject'}
        busy={reopening}
        onConfirm={(reason) => (reasonDialog ? handleReopenCarePlan(reasonDialog.carePlanId, reason) : undefined)}
        onCancel={() => setReasonDialog(null)}
      />

      <GenericListView
        // ListView props
        title="Care Plans"
        icon={<PieChart size={20} />}
        expanded={expanded}
        onToggleExpand={toggleExpanded}
        // Data props
        items={carePlans}
        keyExtractor={(carePlan) => carePlan.carePlanId}
        columns={[
          {
            key: 'date',
            label: 'Date',
            width: 'w-28',
            render: (carePlan) => (
              <span className="text-sm font-medium text-gray-900">
                {new Date(carePlan.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            ),
          },
          {
            key: 'provider',
            label: 'Provider',
            width: 'flex-1 min-w-0',
            render: (carePlan) => (
              <span className="text-sm text-gray-700 truncate pr-3">{carePlan.providerName}</span>
            ),
          },
          {
            key: 'discipline',
            label: 'Discipline',
            width: 'w-28',
            align: 'center',
            render: (carePlan) => (
              <DisciplinePill discipline={carePlan.discipline} />
            ),
          },
          {
            key: 'status',
            label: 'Status',
            width: 'w-24',
            align: 'center',
            render: (carePlan) => (
              <DocStatusPill status={carePlan.status} />
            ),
          },
        ]}
        actions={[
          {
            icon: <Edit size={14} />,
            label: isReadOnlyView ? 'View care plan' : 'Edit care plan',
            onClick: (carePlan) => {
              if (!carePlan.carePlanId) {
                setError('Cannot edit care plan: Missing care plan ID. Please refresh the list.');
                return;
              }
              handleEditCarePlan(carePlan.carePlanId);
            },
            isVisible: () => canReadCareplan,
            className: isReadOnlyView
              ? 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors'
              : 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
          },
          {
            icon: <FileDown size={14} />,
            label: 'Download PDF',
            onClick: (carePlan) => { if (carePlan.carePlanId) handleDownloadCarePlanPdf(carePlan.carePlanId); },
            isVisible: () => canReadCareplan,
            className: 'p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors',
          },
          {
            icon: <CopyPlus size={14} />,
            label: 'Start revision from this plan',
            onClick: (carePlan) => { if (carePlan.carePlanId) handleStartRevision(carePlan); },
            isVisible: (carePlan) => !isReadOnlyView && carePlan.status === CarePlanStatus.COMPLETE,
            className: 'p-1.5 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors',
          },
          {
            icon: <Send size={14} />,
            label: 'Submit care plan',
            onClick: (carePlan) => {
              if (!carePlan.carePlanId) {
                setError('Cannot submit care plan: Missing care plan ID. Please refresh the list.');
                return;
              }
              handleSubmitCarePlan(carePlan.carePlanId);
            },
            isVisible: (carePlan) => !isReadOnlyView && carePlan.status === CarePlanStatus.DRAFT,
            className: 'p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors',
          },
          {
            icon: <RotateCcw size={14} />,
            label: 'Reopen care plan',
            onClick: (carePlan) => {
              if (!carePlan.carePlanId) {
                setError('Cannot reopen care plan: Missing care plan ID. Please refresh the list.');
                return;
              }
              setReasonDialog({
                kind: carePlan.status === CarePlanStatus.COMPLETE ? 'reopen' : 'reject',
                carePlanId: carePlan.carePlanId,
              });
            },
            isVisible: (carePlan) =>
              !isReadOnlyView && canQACareplan && (
                carePlan.status === CarePlanStatus.SUBMITTED ||
                carePlan.status === CarePlanStatus.QA ||
                carePlan.status === CarePlanStatus.COMPLETE
              ),
            className: 'p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors',
          },
        ]}
        // Mobile card config
        mobileCard={{
          primaryText: (carePlan) => carePlan.providerName,
          secondaryText: (carePlan) =>
            new Date(carePlan.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }),
          badges: (carePlan) => [
            {
              label: mapDisciplineToCategory(carePlan.discipline),
              className: getDisciplinePillClassName(carePlan.discipline),
            },
            {
              label: getDocStatusConfig(carePlan.status).label,
              className: getDocStatusPillClassName(carePlan.status),
            },
          ],
          actions: (carePlan) => [
            ...(canReadCareplan
              ? [
                  {
                    icon: <Edit size={18} />,
                    label: isReadOnlyView ? 'View care plan' : 'Edit care plan',
                    onClick: () => {
                      if (!carePlan.carePlanId) {
                        setError('Cannot edit care plan: Missing care plan ID. Please refresh the list.');
                        return;
                      }
                      handleEditCarePlan(carePlan.carePlanId);
                    },
                    className: isReadOnlyView
                      ? 'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors'
                      : 'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors',
                  },
                ]
              : []),
            ...(canReadCareplan &&
            !isReadOnlyView &&
            carePlan.status === CarePlanStatus.DRAFT
              ? [
                  {
                    icon: <Send size={18} />,
                    label: 'Submit care plan',
                    onClick: () => {
                      if (!carePlan.carePlanId) {
                        setError('Cannot submit care plan: Missing care plan ID. Please refresh the list.');
                        return;
                      }
                      handleSubmitCarePlan(carePlan.carePlanId);
                    },
                    className:
                      'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors',
                  },
                ]
              : []),
            ...(canReadCareplan &&
            !isReadOnlyView &&
            canQACareplan &&
            (carePlan.status === CarePlanStatus.SUBMITTED ||
            carePlan.status === CarePlanStatus.QA ||
            carePlan.status === CarePlanStatus.COMPLETE)
              ? [
                  {
                    icon: <RotateCcw size={18} />,
                    label: 'Reopen care plan',
                    onClick: () => {
                      if (!carePlan.carePlanId) {
                        setError('Cannot reopen care plan: Missing care plan ID. Please refresh the list.');
                        return;
                      }
                      setReasonDialog({
                        kind: carePlan.status === CarePlanStatus.COMPLETE ? 'reopen' : 'reject',
                        carePlanId: carePlan.carePlanId,
                      });
                    },
                    className:
                      'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors',
                  },
                ]
              : []),
          ],
        }}
        // Action bar props
        onRefresh={handleRefreshCarePlans}
        refreshing={refreshing}
        loading={loading}
        showNew={canCreateCareplan}
        newButtonLabel="New Care Plan"
        newButtonOptions={carePlanTypeOptions}
        // Pagination props
        hasMore={hasMoreCarePlans}
        onLoadMore={handleLoadMoreCarePlans}
        loadingMore={loadingMore}
        // State props
        error={error}
        onDismissError={() => setError(null)}
        hasLoadedOnce={hasLoadedOnce}
        // Empty state props
        emptyStateIcon={<PieChart size={48} className="text-gray-300" />}
        emptyStateTitle="No care plans available"
        emptyStateMessage="Start by creating a new care plan using the button above."
        // Show list only when in 'past' tab
        showList={activeTab === 'past'}
      >
        {/* New Care Plan Tab - Using the appropriate care plan form component */}
        {activeTab === 'new' && renderCarePlanForm()}
      </GenericListView>
    </>
  );
};

export default CarePlan;